import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext/AuthContext';
import WaButton from '../../components/wa/WaButton';
import WaSwitch from '../../components/wa/WaSwitch';
import { PageWrap, PageHead, Card, SectionHead } from './customerUi';
import {
	NOTIFICATION_CHANNELS,
	fetchCustomerProfile,
	saveCustomerProfile,
} from '../../services/customerProfileService';

/* Account — the parent's own details, editable.
 *
 * REPLACES a pre-shell relic (React-Bootstrap, hardcoded #384971, two disabled
 * inputs, dated Dec 2025 — the last customer page not on customerUi). Two
 * things it carried are fixed here rather than preserved:
 *
 *   1. Log out navigated to `/login`, which is the LEGACY MyMusicStaff widget
 *      page, not `/portal/login`. More.js has always gone to the right place,
 *      so the portal had two log-out buttons landing in different systems.
 *   2. Nothing was editable, so `firstName` was never written by anything —
 *      which is why CustomerDashboard's greeting reads "Hi there". The name was
 *      stored all along under `displayName` (written by claimPortalAccount).
 *
 * Email is deliberately NOT editable: firestore.rules pins it, because
 * familyResolution resolves an intake email to a family through a claimed
 * guardian's users doc. Self-writable email = another family's next purchase
 * landing in this account. The copy says "contact us" rather than pretending
 * the field is merely missing.
 */

const label = {
	display: 'block',
	fontSize: '0.78rem',
	fontWeight: 'var(--fw-bold, 700)',
	color: 'var(--text-muted, #7b8499)',
	marginBottom: 5,
};

const input = {
	width: '100%',
	padding: '9px 11px',
	fontSize: '0.92rem',
	fontFamily: 'inherit',
	color: 'var(--text-body, #4a5570)',
	background: 'var(--surface-input, var(--surface-card, #fff))',
	border: '1px solid var(--cyv-line-cool, var(--cyv-line, #e3ddd4))',
	borderRadius: 'var(--radius-md, 10px)',
	boxSizing: 'border-box',
};

const field = { marginBottom: 13 };
const body = { padding: '4px 15px 16px' };

const Row = ({ children, gap = 10 }) => (
	<div style={{ display: 'flex', gap, flexWrap: 'wrap' }}>{children}</div>
);

const Field = ({ id, text, children, grow = 1, minWidth = 130 }) => (
	<div style={{ ...field, flex: `${grow} 1 ${minWidth}px`, minWidth }}>
		<label htmlFor={id} style={label}>
			{text}
		</label>
		{children}
	</div>
);

const AccountSettings = () => {
	const { currentUser, userProfile, logout, refreshProfile } = useAuth();
	const navigate = useNavigate();
	const uid = currentUser?.uid || null;

	const [form, setForm] = useState(null);
	const [saved, setSaved] = useState(null);
	const [loading, setLoading] = useState(true);
	const [loadError, setLoadError] = useState(false);
	const [saving, setSaving] = useState(false);
	const [saveError, setSaveError] = useState('');
	const [savedOk, setSavedOk] = useState(false);
	const okTimer = useRef(null);

	useEffect(() => {
		if (!uid) {
			setLoading(false);
			return undefined;
		}
		let cancelled = false;
		(async () => {
			try {
				const profile = await fetchCustomerProfile(uid);
				if (cancelled) return;
				if (!profile) {
					setLoadError(true);
				} else {
					setForm(profile);
					setSaved(profile);
				}
			} catch (err) {
				console.error('Error loading account:', err);
				if (!cancelled) setLoadError(true);
			} finally {
				if (!cancelled) setLoading(false);
			}
		})();
		return () => {
			cancelled = true;
		};
	}, [uid]);

	useEffect(() => () => clearTimeout(okTimer.current), []);

	// Compared as JSON rather than field-by-field so a nested address or prefs
	// change counts as dirty without a bespoke comparator per key.
	const dirty = useMemo(
		() => !!form && !!saved && JSON.stringify(form) !== JSON.stringify(saved),
		[form, saved]
	);

	const set = useCallback((patch) => {
		setSavedOk(false);
		setSaveError('');
		setForm((f) => (f ? { ...f, ...patch } : f));
	}, []);

	const setAddress = useCallback(
		(patch) => {
			setSavedOk(false);
			setSaveError('');
			setForm((f) => (f ? { ...f, address: { ...f.address, ...patch } } : f));
		},
		[]
	);

	const setPref = useCallback((key, value) => {
		setSavedOk(false);
		setSaveError('');
		setForm((f) =>
			f ? { ...f, notificationPrefs: { ...f.notificationPrefs, [key]: value } } : f
		);
	}, []);

	const handleSave = async (e) => {
		e.preventDefault();
		if (!uid || !form || saving) return;
		setSaving(true);
		setSaveError('');
		setSavedOk(false);
		try {
			await saveCustomerProfile(uid, form);
			setSaved(form);
			setSavedOk(true);
			// The greeting on Home reads userProfile.firstName, so a save that
			// didn't refresh the context would leave "Hi there" on screen next to
			// a form that clearly says otherwise.
			await refreshProfile?.();
			okTimer.current = setTimeout(() => setSavedOk(false), 4000);
		} catch (err) {
			console.error('Error saving account:', err);
			setSaveError(
				'We couldn’t save your changes just now. Please try again, or contact us if it keeps happening.'
			);
		} finally {
			setSaving(false);
		}
	};

	const handleLogout = async () => {
		try {
			await logout();
		} catch (err) {
			console.error('Logout error:', err);
		}
		// The CYV portal sign-in, NOT /login — that route is the legacy
		// MyMusicStaff widget page and is not where a portal family belongs.
		navigate('/portal/login');
	};

	const email = form?.email || userProfile?.email || currentUser?.email || '';

	return (
		<PageWrap>
			<PageHead title='Account' subtitle='Your details and how we reach you.' />

			{loading && (
				<p style={{ textAlign: 'center', padding: '2rem 0', color: 'var(--text-muted)' }}>
					Loading…
				</p>
			)}

			{!loading && loadError && (
				<Card style={{ padding: '15px', marginBottom: 14 }}>
					<p style={{ margin: 0, color: 'var(--text-body)' }}>
						We couldn’t load your account just now. Please refresh, or{' '}
						<a href='/contact'>contact us</a> if it keeps happening.
					</p>
				</Card>
			)}

			{!loading && !loadError && form && (
				<form onSubmit={handleSave}>
					<Card style={{ marginBottom: 14 }}>
						<SectionHead icon='fa-user' label='Your details' />
						<div style={body}>
							<Row>
								<Field id='acct-first' text='First name'>
									<input
										id='acct-first'
										style={input}
										value={form.firstName}
										autoComplete='given-name'
										onChange={(e) => set({ firstName: e.target.value })}
									/>
								</Field>
								<Field id='acct-last' text='Last name'>
									<input
										id='acct-last'
										style={input}
										value={form.lastName}
										autoComplete='family-name'
										onChange={(e) => set({ lastName: e.target.value })}
									/>
								</Field>
							</Row>

							<Field id='acct-phone' text='Phone'>
								<input
									id='acct-phone'
									style={input}
									type='tel'
									value={form.phone}
									autoComplete='tel'
									placeholder='952-555-0123'
									onChange={(e) => set({ phone: e.target.value })}
								/>
							</Field>

							<div style={field}>
								<span style={label}>Email</span>
								<input style={{ ...input, opacity: 0.65 }} value={email} disabled />
								<p
									style={{
										margin: '6px 0 0',
										fontSize: '0.78rem',
										color: 'var(--text-muted, #9aa2b1)',
										lineHeight: 1.5,
									}}>
									Your email is how we match you to your family’s records, so we
									change it for you — <a href='/contact'>get in touch</a> and we’ll
									take care of it.
								</p>
							</div>
						</div>
					</Card>

					<Card style={{ marginBottom: 14 }}>
						<SectionHead icon='fa-envelope' label='Mailing address' />
						<div style={body}>
							<Field id='acct-line1' text='Street address'>
								<input
									id='acct-line1'
									style={input}
									value={form.address.line1}
									autoComplete='address-line1'
									onChange={(e) => setAddress({ line1: e.target.value })}
								/>
							</Field>
							<Field id='acct-line2' text='Apt, suite, etc. (optional)'>
								<input
									id='acct-line2'
									style={input}
									value={form.address.line2}
									autoComplete='address-line2'
									onChange={(e) => setAddress({ line2: e.target.value })}
								/>
							</Field>
							{/* City gets its own row and State/ZIP pair below it. All three on
							    one row does not fit a 375px column: measured against the real
							    stylesheets, the third field wrapped and ZIP was left stranded
							    alone at full width, which reads as a mistake. Two clean rows
							    beat one row that collapses into an ugly three. */}
							<Field id='acct-city' text='City'>
								<input
									id='acct-city'
									style={input}
									value={form.address.city}
									autoComplete='address-level2'
									onChange={(e) => setAddress({ city: e.target.value })}
								/>
							</Field>
							<Row>
								<Field id='acct-state' text='State' minWidth={90}>
									<input
										id='acct-state'
										style={input}
										value={form.address.state}
										autoComplete='address-level1'
										onChange={(e) => setAddress({ state: e.target.value })}
									/>
								</Field>
								<Field id='acct-zip' text='ZIP' minWidth={110}>
									<input
										id='acct-zip'
										style={input}
										value={form.address.postalCode}
										autoComplete='postal-code'
										inputMode='numeric'
										onChange={(e) => setAddress({ postalCode: e.target.value })}
									/>
								</Field>
							</Row>
						</div>
					</Card>

					<Card style={{ marginBottom: 14 }}>
						<SectionHead icon='fa-bell' label='Email preferences' />
						<div style={body}>
							{NOTIFICATION_CHANNELS.map((ch) => (
								<div
									key={ch.key}
									style={{
										display: 'flex',
										alignItems: 'flex-start',
										gap: 12,
										padding: '9px 0',
									}}>
									<span style={{ flex: 1, minWidth: 0 }}>
										<span
											style={{
												display: 'block',
												fontSize: '0.9rem',
												fontWeight: 'var(--fw-bold, 700)',
												color: 'var(--text-heading, #384971)',
											}}>
											{ch.label}
										</span>
										<span
											style={{
												display: 'block',
												fontSize: '0.78rem',
												color: 'var(--text-muted, #7b8499)',
												marginTop: 2,
												lineHeight: 1.5,
											}}>
											{ch.help}
										</span>
									</span>
									<WaSwitch
										checked={form.notificationPrefs[ch.key] !== false}
										aria-label={ch.label}
										onToggle={(next) => setPref(ch.key, next)}
									/>
								</div>
							))}
							<p
								style={{
									margin: '8px 0 0',
									fontSize: '0.78rem',
									color: 'var(--text-muted, #9aa2b1)',
									lineHeight: 1.55,
								}}>
								Billing, account and studio closure emails always come through. These
								settings cover studio news, lesson recaps and lesson reminders only.
							</p>
						</div>
					</Card>

					{saveError && (
						<Card style={{ padding: '13px 15px', marginBottom: 12 }}>
							<p style={{ margin: 0, color: 'var(--text-body)', fontSize: '0.88rem' }}>
								{saveError}
							</p>
						</Card>
					)}

					<div
						style={{
							display: 'flex',
							alignItems: 'center',
							gap: 12,
							marginBottom: 18,
						}}>
						<WaButton
							type='submit'
							variant='brand'
							loading={saving}
							disabled={!dirty || saving}>
							Save changes
						</WaButton>
						{savedOk && (
							<span
								role='status'
								style={{
									fontSize: '0.85rem',
									fontWeight: 'var(--fw-bold, 700)',
									color: 'var(--cyv-teal-600, #2f7d77)',
								}}>
								Saved
							</span>
						)}
					</div>
				</form>
			)}

			<Card>
				<button
					type='button'
					onClick={handleLogout}
					style={{
						display: 'flex',
						alignItems: 'center',
						gap: 13,
						padding: '13px 15px',
						width: '100%',
						background: 'none',
						border: 'none',
						cursor: 'pointer',
						fontFamily: 'inherit',
						textAlign: 'left',
					}}>
					<span
						style={{
							width: 38,
							height: 38,
							borderRadius: 11,
							background:
								'color-mix(in srgb, var(--cyv-danger, #d9776a) 12%, var(--surface-card, #fff))',
							color: 'var(--cyv-danger, #d9776a)',
							display: 'flex',
							alignItems: 'center',
							justifyContent: 'center',
							flex: '0 0 auto',
						}}>
						<i
							className='fa-sharp fa-solid fa-arrow-right-from-bracket'
							style={{ fontSize: 14 }}
							aria-hidden='true'
						/>
					</span>
					<span
						style={{
							fontSize: '0.92rem',
							fontWeight: 'var(--fw-bold, 700)',
							color: 'var(--cyv-danger, #d9776a)',
						}}>
						Log out
					</span>
				</button>
			</Card>
		</PageWrap>
	);
};

export default AccountSettings;
